// Shared UI primitive (reused across all dashboard pages) — MIS-809.
//
// A generic TanStack Table wrapper. Pass `columns` (ColumnDef[]) + `data`; it
// renders a header, rows, an empty state, and a simple prev/next pager (client
// pagination via getPaginationRowModel). Generic over `<TData>` so every list
// page (domains, landers, leads, …) reuses the same table. `@tanstack/react-table`
// is already installed — no extra deps.

import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Button } from './Button';

export interface DataTableProps<TData> {
  // biome-ignore lint/suspicious/noExplicitAny: ColumnDef's value type is per-column; `any` here is TanStack's own public signature for a heterogeneous column set.
  columns: ColumnDef<TData, any>[];
  data: TData[];
  /** Rows per page for the built-in client pager. */
  pageSize?: number;
  /** Shown when `data` is empty. */
  emptyMessage?: string;
}

export function DataTable<TData>({
  columns,
  data,
  pageSize = 10,
  emptyMessage = 'No results.',
}: DataTableProps<TData>): React.ReactElement {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
  });

  const rows = table.getRowModel().rows;
  const showPager = data.length > pageSize;

  return (
    <div className="w-full">
      <div className="overflow-hidden rounded-lg border border-[var(--border-soft)] bg-[var(--surface)]">
        <table className="w-full text-sm">
          <thead className="border-b border-[var(--border-soft)] bg-[var(--surface-2)]">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 py-3 text-left font-medium text-[var(--muted)]"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-[var(--border-soft)]">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-[var(--muted)]">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="hover:bg-[var(--surface-3)]">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3 text-[var(--text)]">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showPager && (
        <div className="flex items-center justify-end gap-2 pt-3">
          <span className="text-xs text-[var(--muted)]">
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
